import { BotProtection } from './bot-protection';
import { isError } from 'src/utilities/type-check';
import { buildQueryString, QueryParams } from 'src/utilities/url-helper';

// Configure the baseDomain to reflect environment
export const baseUrl = process.env.SERVER_URL;

const interceptors = [];
let botProtection: Promise<BotProtection> = null;

async function interceptResponse(response: Response) {
	for (const intercept of interceptors)
		await intercept(response.clone());
	return response;
}

export type RequestOptions = RequestInit & {
	query?: QueryParams;
	bypassCaptcha?: boolean;
	authorize?: boolean;
};

const javascriptDateExpr = /^\/Date\((\d+)\)\/$/;

export function convertToDate(str: string): Date | null {
	if (javascriptDateExpr.test(str)) {
		const num = parseInt(javascriptDateExpr.exec(str)[1], 10);
		if (!isNaN(num))
			return new Date(num);
	}
	return null;
}

export async function serviceRequest(fetchEndpoint, data, options: RequestOptions = null) {
	const fetchInit: RequestOptions = Object.assign({
		method: 'POST',
		credentials: options && options.authorize === false ? 'omit' : 'include',
		headers: { 'Content-Type': 'application/json' }
	}, options);

	if (!options || options.bypassCaptcha !== true) {
		if (botProtection && !(await botProtection).response) {
			(await botProtection).botDetected();
			return Promise.reject(new Error('A request was attempted before verifying user is not a bot.'));
		}

		if (botProtection)
			fetchInit.headers[(await botProtection).response.method] = (await botProtection).response.token;
	}

	if (fetchInit.method === 'POST')
		fetchInit.body = JSON.stringify(data);

	if (fetchInit.query)
		fetchEndpoint += buildQueryString(fetchInit.query);

	const requestUrl = `${baseUrl}${fetchEndpoint}`;

	return fetch(requestUrl, fetchInit)
		.then(interceptResponse)
		.then(async response => {
			if (response.ok)
				return response.json();

			// Throw a specific error which contains details about the error (i.e. status code)
			const statusCode = response.status;
			const responseText = await response.text();
			throw new ServiceRequestError(requestUrl, statusCode, responseText, location.href);
		});
}

export function interceptResponses(interceptor: (response: Response) => Promise<any>) {
	interceptors.push(interceptor);
}

export function enableBotProtection() {
	if (!botProtection)
		botProtection = serviceRequest('svc/auth/bot-challenge', null, { method: 'GET' }).then(challengeData => new BotProtection(challengeData));
	return botProtection;
}

/**
 * An error that represents failure of a service request
 */
export class ServiceRequestError extends Error {
	/**
	 * The service request URL that failed
	 */
	readonly requestUrl: string;

	/**
	 * The status code that was returned
	 */
	readonly statusCode: number;

	/**
	 * The response text that was returned, if any
	 */
	readonly responseText: string;

	/**
	 * The URL of the page that issued the request
	 */
	readonly referrerUrl: string;

	/**
	 * Constructs a new service request error
	 */
	constructor(requestUrl: string, statusCode: number, responseText: string, referrerUrl: string) {
		super(`${statusCode}: ${responseText || 'Request failed'}`);
		this.requestUrl = requestUrl;
		this.statusCode = statusCode;
		this.responseText = responseText;
		this.referrerUrl = referrerUrl;
	}
}

/**
 * Checks whether the given object is a `ServiceRequestError`
 * @param err The object to check
 */
export function isServiceRequestError(err: any): err is ServiceRequestError {
	return isError(err) && err.constructor.name === 'ServiceRequestError';
}
